import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import { useFlags } from '@flagsmith/flagsmith/react';


import { Flex, FlexColumn } from 'src/components/flex';
import { ShowingIcon, SnippetsIcon } from 'src/icons';
import { ELeadStage, ELeadStageChangeReason } from 'src/services/api/leads/types';
import { useUpdateLeadStage } from 'src/services/api/leads/use-update-lead-stage';
import { EMessageDirection } from 'src/services/api/messages/types';
import { useReplySuggestions, type IReplySuggestion } from 'src/services/api/messages/use-reply-suggestions';
import { EReplySuggestionStatus, useUpdateReplySuggestion } from 'src/services/api/messages/use-update-reply-suggestion';
import {
    useCreateTourSuggestions,
    useDismissTourSuggestion,
    useOpenTourSuggestion,
} from 'src/services/api/messages/use-tour-slot-suggestions';
import { useTouringHours } from 'src/services/api/touring-hours/use-touring-hours';
import { passesAvailabilityGate } from 'src/services/tour-availability/availability-gate';
import { formatSuggestionMessage } from 'src/services/tour-availability/format-suggestion-message';
import type { TourSlotSuggestionOption } from 'src/services/tour-availability/types';
import { TourSlotSuggestions } from 'src/pages/landlord/leads/messages/components/nudges/TourSlotSuggestions';
import { useActionSuggestions } from 'src/services/action-suggestions/use-action-suggestions';
import { useSetShowingStatus } from 'src/services/api/showings/use-set-showing-status';
import { passesReplyGate } from 'src/services/reply-suggestions/reply-gate';
import ReplySuggestions from 'src/pages/landlord/leads/messages/components/input/ReplySuggestions';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import { EActionSuggestionAction, EActionSuggestionType } from 'src/services/action-suggestions/types';
import { SelectLeadStage } from 'src/pages/landlord/leads/messages/components/nudges/NudgesStageSelect';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context';
import { useComposer } from 'src/pages/landlord/leads/messages/context/composer-hooks';
import { LEAD_STAGE_LABELS } from 'src/components/input/select-lead-stage/leadStageOptions';
import { useLeadFactSuggestions } from 'src/services/lead-facts/use-lead-fact-suggestions';
import type { IFactSuggestion } from 'src/services/lead-facts/types';
import { EFeatureFlag, FORCE_TOUR_TIME_SUGGESTIONS } from 'src/utils/feature-flags';
import { getEffectiveTourTimezone } from 'src/utils/format-date-time';
import { useDecideFact } from 'src/services/api/leads/use-decide-fact';
import { LeadFactSuggestions } from 'src/pages/landlord/leads/messages/components/nudges/LeadFactSuggestions';
import { useMarkJustAdded, rowKeyForFact } from 'src/components/lead-sidebar/just-added-facts-context';

import styles from 'src/pages/landlord/leads/messages/components/nudges/Nudges.module.css';
import conversationBoxLogo from 'src/assets/img/conversation/box_logo.png';
import conversationBtnLogo from 'src/assets/img/conversation/btn_logo.png';
import conversationBtnLogoRed from 'src/assets/img/conversation/btn_logo_red.png';


type Props = {
    openTourModal: () => void;
}

export const Nudges = (props: Props) => {

    const {
        lead,
        loggedInUser,
        setIsSnippetsModalOpen,
        showings,
        messages,
        userSettings
    } = useLeadMessagesPageContext();
    const { acceptSuggestion } = useComposer();

    const { openTourModal } = props;


    const notification = useActionSuggestions({
        lead,
        showings,
        messages
    });

    const notifMidsize = useMediaQuery((theme: any) => theme.breakpoints.down('notificationsMidSize'));
    const notifMidsize2 = useMediaQuery((theme: any) => theme.breakpoints.down('notificationsMidSize2'));
    const stackActionButtons = useMediaQuery((theme: any) => theme.breakpoints.down('notificationsMidSize3'));

    const { mutate: updateLead } = useUpdateLeadStage(lead.uuid);

    const featureFlags = useFlags([EFeatureFlag.TOUR_TIME_SUGGESTIONS]);
    const isTourTimeSuggestionsEnabled =
        (featureFlags.tour_time_suggestions?.enabled ?? false) || FORCE_TOUR_TIME_SUGGESTIONS;
    const { data: openTourSuggestion } = useOpenTourSuggestion(
        lead.uuid,
        isTourTimeSuggestionsEnabled && loggedInUser.id === lead.owner
    );

    const { data: touringHours } = useTouringHours(lead.property_id);
    const { timezone: tourTimezone } = getEffectiveTourTimezone(touringHours?.timezone, userSettings);
    const { suggestions: factSuggestions, isLoading: areFactSuggestionsLoading } =
        useLeadFactSuggestions(lead, messages);
    const { mutate: decideFactMutation } = useDecideFact(lead.uuid);
    const markJustAdded = useMarkJustAdded();
    /*
     * Optimistically hide a chip the moment it's acted on, until the lead refetch
     * reflects the new status. Keyed by key+value (not key alone) so a later
     * suggestion of the same field with a different value still shows.
     */
    const [dismissedFacts, setDismissedFacts] = useState<Set<string>>(new Set());
    const dismissTokenOf = (s: IFactSuggestion) => `${s.key}::${s.value}`;

    const visibleFactSuggestions = useMemo(
        () => factSuggestions.filter((s) => !dismissedFacts.has(dismissTokenOf(s))),
        [factSuggestions, dismissedFacts]
    );

    /*
     * Every displayed chip is a stored suggestion row (regex values are persisted as
     * pending rows first), so a decision is always a PATCH by the row id.
     */
    const decideFact = (suggestion: IFactSuggestion, status: 'accepted' | 'rejected') => {
        if (suggestion.id === undefined) {
            return;
        }
        decideFactMutation({ id: suggestion.id, status });
        setDismissedFacts((prev) => new Set(prev).add(dismissTokenOf(suggestion)));
    };

    const handleAcceptFact = (suggestion: IFactSuggestion) => {
        decideFact(suggestion, 'accepted');
        markJustAdded([rowKeyForFact(suggestion.key)]);
    };

    const handleDismissFact = (suggestion: IFactSuggestion) => {
        decideFact(suggestion, 'rejected');
    };

    const lastInboundMessage = useMemo(
        () =>
            [...messages]
                .reverse()
                .find((m) => m.direction === EMessageDirection.IN && !!m.uuid),
        [messages]
    );
    /*
     * Only query when the newest message in the thread is the inbound one — an
     * unanswered question. Once the agent replies, the newest message is outbound so
     * the query disables, but it stays keyed to the inbound message: react-query keeps
     * the already-fetched suggestion in cache, so it stays visible for the rest of the
     * session until the agent accepts/dismisses it. On a refresh it starts disabled, so
     * it is never re-fetched and does not re-appear.
     */
    const lastMessage = messages[messages.length - 1];
    const lastMessageIsInbound =
        lastMessage?.direction === EMessageDirection.IN && !!lastMessage.uuid;
    const replyGatePasses =
        !!lastInboundMessage &&
        lastMessageIsInbound &&
        passesReplyGate(lastInboundMessage.contents);
    const { data: replySuggestions, isLoading: areReplySuggestionsLoading } = useReplySuggestions(
        lastInboundMessage?.uuid,
        replyGatePasses
    );
    const { mutate: updateReplySuggestion } = useUpdateReplySuggestion();
    const [decidedReplyIds, setDecidedReplyIds] = useState<Set<number>>(new Set());

    const isInSchedulingWindow =
        lead.stage === ELeadStage.CONTACTED || lead.stage === ELeadStage.RESPONSIVE || lead.stage === ELeadStage.TOUR_REQUESTED;
    const { mutate: createTourSuggestions } = useCreateTourSuggestions(lead.uuid);
    const { mutate: dismissTourSuggestion } = useDismissTourSuggestion(lead.uuid);

    const availabilityGatePasses =
        isTourTimeSuggestionsEnabled &&
        !!lastInboundMessage &&
        lastMessageIsInbound &&
        (lead.stage === ELeadStage.CONTACTED || lead.stage === ELeadStage.RESPONSIVE) &&
        passesAvailabilityGate(lastInboundMessage.contents);
    /*
     * Fire the extraction once per inbound message, only when no suggestion is open yet.
     * Keying the ref on the message uuid keeps a re-render or refetch from re-running
     * the LLM for the same conversation state.
     */
    const requestedForMessageRef = useRef<string | null>(null);
    const lastInboundUuid = lastInboundMessage?.uuid;
    useEffect(() => {
        if (
            availabilityGatePasses &&
            !openTourSuggestion &&
            lastInboundUuid &&
            requestedForMessageRef.current !== lastInboundUuid
        ) {
            requestedForMessageRef.current = lastInboundUuid;
            createTourSuggestions({ messageUuid: lastInboundUuid });
        }
    }, [
        availabilityGatePasses,
        openTourSuggestion,
        lastInboundUuid,
        createTourSuggestions,
    ]);

    /*
     * Only show the suggestion while the lead is in the tour-scheduling window; once the
     * stage moves past it (tour scheduled, not interested, etc.) the suggestion hides.
     * Elapsed slots are dropped too — a slot whose end time is already in the past is no
     * longer offerable — so a suggestion is only shown for its still-future slots and
     * hides entirely once every slot has passed.
     */
    const suggestedSlots = useMemo(() => {
        if (!isInSchedulingWindow) {
            return [];
        }
        const now = dayjs();
        return (openTourSuggestion?.options ?? []).filter((slot) =>
            dayjs(slot.end_time).isAfter(now)
        );
    }, [isInSchedulingWindow, openTourSuggestion]);

    const [, setSearchParams] = useSearchParams();
    const openedTabForSuggestionRef = useRef<number | null>(null);
    useEffect(() => {
        if (
            openTourSuggestion &&
            suggestedSlots.length > 0 &&
            openedTabForSuggestionRef.current !== openTourSuggestion.id
        ) {
            openedTabForSuggestionRef.current = openTourSuggestion.id;
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('sidebarTab', 'tours');
                    return next;
                },
                { replace: true }
            );
        }
    }, [openTourSuggestion, suggestedSlots.length, setSearchParams]);

    const handleSelectTourSlot = (slot: TourSlotSuggestionOption) => {
        acceptSuggestion(formatSuggestionMessage(slot.start_time, tourTimezone));
    };

    const handleDismissTourSlots = () => {
        if (openTourSuggestion) {
            dismissTourSuggestion({ suggestionId: openTourSuggestion.id });
        }
    };

    // Show one suggestion at a time; the next surfaces after the current is decided.
    const visibleReplySuggestions = useMemo(
        () => (replySuggestions || []).filter((s) => !decidedReplyIds.has(s.id)).slice(0, 1),
        [replySuggestions, decidedReplyIds]
    );

    const decideReply = (suggestion: IReplySuggestion, status: EReplySuggestionStatus) => {
        setDecidedReplyIds((prev) => new Set(prev).add(suggestion.id));
        updateReplySuggestion(
            { id: suggestion.id, status },
            {
                // Re-surface the suggestion if the decision failed to persist, so the
                // agent can retry rather than losing it for the rest of the session.
                onError: () =>
                    setDecidedReplyIds((prev) => {
                        const next = new Set(prev);
                        next.delete(suggestion.id);
                        return next;
                    }),
            }
        );
    };

    const handleSelectReply = (suggestion: IReplySuggestion) => {
        // Prepend the suggestion above whatever is already composed; the composer owns
        // ordering, spacing, and signature preservation.
        acceptSuggestion(suggestion.content);
        decideReply(suggestion, EReplySuggestionStatus.SELECTED);
    };

    const handleDismissReply = (suggestion: IReplySuggestion) => {
        decideReply(suggestion, EReplySuggestionStatus.REJECTED);
    };

    const isWarning = notification?.type === EActionSuggestionType.warning;

    useEffect(() => {
        setDismissedFacts(new Set());
        setDecidedReplyIds(new Set());
    }, [lead.uuid]);

    /**
     * This function can be called in two scenarios:
     * 1. When the nudge notification specifies a specific stage to change to.
     * 2. When the user selects a stage from the nudge dropdown.
     */
    const handleChangeStageWithNudge = (newStage: ELeadStage, reason: ELeadStageChangeReason) => {
        analyticsClient.track(
            AnalyticsEvent.USED_SMART_NOTIFICATION_CHANGE_STAGE
        );
        updateLead({
            stage: newStage,
            reason: reason,
        });
    };

    const { mutate: setShowingStatus } = useSetShowingStatus();

    const dispatchAction = useCallback(
        (actionType?: EActionSuggestionAction) => {
            if (!notification || !actionType) {
                return;
            }
            switch (actionType) {
                case EActionSuggestionAction.OPEN_SNIPPETS:
                    analyticsClient.track(
                        AnalyticsEvent.USED_SMART_NOTIFICATION_OPEN_SNIPPETS
                    );
                    setIsSnippetsModalOpen(true);
                    break;
                case EActionSuggestionAction.CHANGE_SPECIFIC_STAGE:
                    analyticsClient.track(
                        AnalyticsEvent.USED_SMART_NOTIFICATION_CHANGE_STAGE
                    );
                    handleChangeStageWithNudge(notification.changeToSpecificStage!, ELeadStageChangeReason.MANUAL_RECOMMEND_NUDGE);
                    break;
                case EActionSuggestionAction.OPEN_TOUR_MODAL:
                    analyticsClient.track(
                        AnalyticsEvent.USED_SMART_NOTIFICATION_SCHEDULE_TOUR
                    );
                    openTourModal();
                    break;
                default:
                    break;
            }
        },
        [notification]
    );

    const primaryAction = useMemo(() => {
        if (notification?.changeToSpecificStage) {
            return <Button variant="outlined" color={isWarning ? 'error' : 'primary'} onClick={() => dispatchAction(EActionSuggestionAction.CHANGE_SPECIFIC_STAGE)}>
                {isWarning ?
                    (<img src={conversationBtnLogoRed} alt="image" width={15} height={15} />) :
                    (<img src={conversationBtnLogo} alt="image" width={15} height={15} />)}
                <Typography variant="body2" fontWeight={600} noWrap component="div" color="inherit">
                    {`Change stage to "${LEAD_STAGE_LABELS[notification?.changeToSpecificStage]}"`}
                </Typography>
            </Button>;
        }
        return (
            <SelectLeadStage
                value={lead.stage}
                onChange={(newStage) => handleChangeStageWithNudge(newStage, ELeadStageChangeReason.MANUAL_SELECT_NUDGE)}
                isWarning={isWarning}
            />
        );
    }, [notification, dispatchAction, updateLead, isWarning]);

    const secondaryAction = useMemo(() => {
        if (isWarning) {
            return null;
        }
        return <Button variant="contained" color={isWarning ? 'error' : 'primary'} onClick={() => dispatchAction(EActionSuggestionAction.OPEN_SNIPPETS)}>
            <Flex gap={1} alignItems="center">
                <SnippetsIcon width={15} height={15} />
                <Typography variant="body2" noWrap component="div" color="inherit">
                    Open Snippets
                </Typography>
            </Flex>
        </Button>;
    }, [notification, dispatchAction, isWarning]);

    const scheduleTourAction = useMemo(() => {
        // Warning nudges (e.g. a lead asking to stop) should offer only the
        // stage-change action, not a competing "schedule a tour" prompt. Some
        // nudges (e.g. tour attended) also opt out explicitly.
        if (isWarning || notification?.hideScheduleTour) {
            return null;
        }
        const hasUpcomingShowings = showings.some(o => o.lead_uuid === lead.uuid && !o.is_canceled && dayjs(o.start_time).isAfter(dayjs()));
        const inLogicallyFinishedStage = [
            ELeadStage.NOT_QUALIFIED,
            ELeadStage.APPLICATION_COMPLETED,
            ELeadStage.NOT_INTERESTED,
            ELeadStage.LEASE_SIGNED
        ].includes(lead.stage);
        if (lead.stage == ELeadStage.CONTACTED || (!hasUpcomingShowings && !inLogicallyFinishedStage)) {
            return <Button variant="outlined" color={isWarning ? 'error' : 'primary'} onClick={() => dispatchAction(EActionSuggestionAction.OPEN_TOUR_MODAL)}>
                <Flex gap={1} alignItems="center">
                    <ShowingIcon width={15} height={15} />
                    <Typography variant="body2" noWrap component="div" color="inherit">
                        Schedule a Tour
                    </Typography>
                </Flex>
            </Button>;
        }
        return null;
    }, [lead, showings, isWarning, notification]);

    const tourStatusAction = useMemo(() => {
        const action = notification?.tourStatusAction;
        // Marking attendance hits validate_can_edit_tour, which allows the lead
        // owner only. Unlike the tour modal (which warns non-owners itself), these
        // buttons fire the mutation directly, so hide them for non-owners.
        if (!action || loggedInUser.id !== lead.owner) {
            return null;
        }
        return (
            <>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowingStatus({ tourId: action.tourId, isAttended: true })}
                >
                    <Typography variant="body2" component="div" color="inherit">
                        Attended
                    </Typography>
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowingStatus({ tourId: action.tourId, isAttended: false })}
                >
                    <Typography variant="body2" component="div" color="inherit">
                        Missed
                    </Typography>
                </Button>
            </>
        );
    }, [notification, setShowingStatus, loggedInUser.id, lead.owner]);

    /*
     * Both suggestion queries fetch on mount, so falling through to the regular
     * notification here would flash it REF-7346
     */
    if (areFactSuggestionsLoading || areReplySuggestionsLoading) {
        return null;
    }

    /*
     * Single-focus: when there are fact suggestions, the nudge shows only those —
     * the regular notification (title/buttons) and reply suggestions are hidden.
     */
    if (visibleFactSuggestions.length > 0) {
        return (
            <Box p={1} className={styles.outer}>
                <FlexColumn rowGap={1} className={styles.inner} maxWidth="100%">
                    <Flex alignItems="center" justifyItems="space-between" columnGap={2}>
                        {!notifMidsize2 && <img src={conversationBoxLogo} alt="image" width={36} height={36} />}
                        <LeadFactSuggestions
                            suggestions={visibleFactSuggestions}
                            onAccept={handleAcceptFact}
                            onDismiss={handleDismissFact}
                        />
                    </Flex>
                </FlexColumn>
            </Box>
        );
    }

    /*
     * Single-focus, mirroring fact suggestions: when there are reply suggestions the
     * nudge shows only those — the notification title/action buttons are hidden. A
     * warning is more urgent than a canned reply, so it always wins over suggestions.
     */
    if (!isWarning && visibleReplySuggestions.length > 0) {
        return (
            <Box p={1} className={styles.outer}>
                <FlexColumn rowGap={1} className={styles.inner} maxWidth="100%">
                    <Flex alignItems="center" justifyItems="space-between" columnGap={2}>
                        {!notifMidsize2 && <img src={conversationBoxLogo} alt="image" width={36} height={36} />}
                        <ReplySuggestions
                            suggestions={visibleReplySuggestions}
                            onSelect={handleSelectReply}
                            onDismiss={handleDismissReply}
                        />
                    </Flex>
                </FlexColumn>
            </Box>
        );
    }

    /*
     * Single-focus, mirroring the other suggestion nudges: when there are matching
     * tour slots the nudge shows only those. A warning always wins over suggestions.
     */
    if (!isWarning && suggestedSlots.length > 0) {
        return (
            <Box p={1} className={styles.outer}>
                <FlexColumn rowGap={1} className={styles.inner} maxWidth="100%">
                    <Flex alignItems="center" justifyItems="space-between" columnGap={2}>
                        {!notifMidsize2 && <img src={conversationBoxLogo} alt="image" width={36} height={36} />}
                        <TourSlotSuggestions
                            slots={suggestedSlots}
                            timezone={tourTimezone}
                            onSelect={handleSelectTourSlot}
                            onDismiss={handleDismissTourSlots}
                        />
                    </Flex>
                </FlexColumn>
            </Box>
        );
    }

    if (!notification) {
        return null;
    }

    return (
        <Box p={1} className={`${styles.outer} ${notifMidsize ? styles.outerLessLg : ''} ${notifMidsize2 ? styles.outerSmall : ''}`}>
            <FlexColumn rowGap={1} className={`${styles.inner} ${isWarning ? styles.warning : ''}`}
                justifyContent="space-between"
                maxWidth={'100%'}
            >
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" justifyItems="space-between" columnGap={2}>
                        <FlexColumn flex={1} rowGap={0.5}>
                            <Typography fontSize={14} fontWeight={600} component="div">
                                {notification.title}
                            </Typography>
                            <Typography fontSize={12} fontWeight={400} component="div">
                                {notification.description}
                            </Typography>
                        </FlexColumn>
                    </Flex>
                    <Flex columnGap={2} className={`${styles.actionButtons} ${stackActionButtons ? styles.actionButtonsStacked : ''}`}>
                        {tourStatusAction ?? (
                            <>
                                {scheduleTourAction}
                                {secondaryAction}
                                {primaryAction}
                            </>
                        )}
                    </Flex>
                </Flex>
            </FlexColumn>
        </Box>
    );
};


